import { useCallback, useRef } from 'react';

const STORAGE_KEY = 'refdash:sound_enabled';

/** Read the persisted preference (default: on). */
export function isSoundEnabled(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === null ? true : v === '1';
  } catch {
    return true;
  }
}

/** Persist the preference. */
export function setSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // storage unavailable
  }
}

/**
 * Tri-tone alert — hospital-style three-note pattern, repeated twice.
 * Synthesised via Web Audio API, no audio file needed.
 */
function playChime(): void {
  try {
    const ctx = new AudioContext();
    const now = ctx.currentTime;

    const tones = [
      { freq: 880, start: 0, dur: 0.2 },
      { freq: 1100, start: 0.25, dur: 0.2 },
      { freq: 880, start: 0.5, dur: 0.2 },
      { freq: 880, start: 0.9, dur: 0.2 },
      { freq: 1100, start: 1.15, dur: 0.2 },
      { freq: 880, start: 1.4, dur: 0.2 },
    ];

    for (const { freq, start, dur } of tones) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.35, now + start);
      gain.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    }

    setTimeout(() => ctx.close(), 2500);
  } catch {
    // AudioContext unavailable
  }
}

/**
 * Hook that returns a `playSound` callback.
 * Respects the localStorage preference and guards against rapid repeats.
 */
export function useNotificationSound() {
  const lastPlayedRef = useRef(0);

  const playSound = useCallback(() => {
    if (!isSoundEnabled()) return;

    // Debounce: skip if played within the last 3 seconds
    const now = Date.now();
    if (now - lastPlayedRef.current < 3000) return;
    lastPlayedRef.current = now;

    playChime();
  }, []);

  return { playSound };
}
